import { Refresh } from '@mui/icons-material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExamDifficultyLevelView, HighestExamResultView } from '../../graphql/types.gen.ts';
import { useIsUserLoggedIn } from '../../helpers/useValidateToken.ts';
import { GENERAL_GROUP_NAME } from '../../index.tsx';
import {
	useExamsForFiltersFromGroupQuery,
	useExamsForFiltersFromPublicGroupUserNotLoggedInQuery,
} from '../../pages/Game/game.gen.ts';
import './Drawer.scss';
import { Drawer } from './Drawer.tsx';
import { TestItem } from './TestItem.tsx';

type AllTestsDrawerProps = {
	drawTestById: (id: string) => void;
	drawTest: () => void;
	chosenGroup: string;
	isOpened: boolean;
	onClose: () => void;
	filters: { tags: string[]; difficultyLevels: ExamDifficultyLevelView[] };
	onFiltersChange: (filters: { tags: string[]; difficultyLevels: ExamDifficultyLevelView[] }) => void;
	resultSaveVersion: number;
};

const LEVELS = [
	{ id: 'level-1', label: 'LATWY', value: ExamDifficultyLevelView.Easy, className: 'allTests__tag--level-1' },
	{ id: 'level-2', label: 'SREDNI', value: ExamDifficultyLevelView.Medium, className: 'allTests__tag--level-2' },
	{ id: 'level-3', label: 'TRUDNY', value: ExamDifficultyLevelView.Hard, className: 'allTests__tag--level-3' },
] as const;

const LEVEL_IDS: string[] = LEVELS.map((level) => level.id);
const FILTERS_STORAGE_KEY = 'all-tests-drawer-filters';

const readStoredFilters = () => {
	if (typeof window === 'undefined') return null;

	try {
		const storedFilters = window.localStorage.getItem(FILTERS_STORAGE_KEY);
		if (storedFilters === null) return null;

		const parsedFilters = JSON.parse(storedFilters) as unknown;
		if (Array.isArray(parsedFilters) && parsedFilters.every((filter) => typeof filter === 'string')) {
			return parsedFilters;
		}
	} catch {
		window.localStorage.removeItem(FILTERS_STORAGE_KEY);
	}

	return null;
};

export const AllTestsDrawer = ({
	drawTestById,
	drawTest,
	chosenGroup,
	isOpened,
	onClose,
	filters,
	onFiltersChange,
	resultSaveVersion,
}: AllTestsDrawerProps) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const { isUserLoggedIn } = useIsUserLoggedIn();
	const shouldUsePrivateGroupQuery = isUserLoggedIn;
	const shouldUsePublicLoggedOutQuery = !isUserLoggedIn;
	const [localFilters, setLocalFilters] = useState<string[]>(() => {
		const storedFilters = readStoredFilters();
		if (storedFilters) {
			return storedFilters;
		}

		const difficultyLevelFilters = LEVELS.filter((level) => filters.difficultyLevels.includes(level.value)).map(
			(level) => level.id
		);
		return [...filters.tags, ...difficultyLevelFilters];
	});

	const selectedTags = useMemo(() => localFilters.filter((filter) => !LEVEL_IDS.includes(filter)), [localFilters]);
	const selectedDifficultyLevels = useMemo(
		() => LEVELS.filter((level) => localFilters.includes(level.id)).map((level) => level.value),
		[localFilters]
	);

	useEffect(() => {
		onFiltersChange({
			tags: selectedTags,
			difficultyLevels: selectedDifficultyLevels,
		});
	}, [onFiltersChange, selectedDifficultyLevels, selectedTags]);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(localFilters));
	}, [localFilters]);

	const {
		data: allTestsData,
		loading: allTestsLoading,
		refetch: refetchAllTestsData,
	} = useExamsForFiltersFromGroupQuery({
		skip: !shouldUsePrivateGroupQuery,
		variables: {
			code: chosenGroup || GENERAL_GROUP_NAME,
			input: {
				filters: {
					difficultyLevel: [],
					ignoreExamsConnectedToLessons: false,
					tags: [],
				},
				pagination: {
					limit: 500,
					offset: 0,
				},
			},
		},
	});

	const {
		data: allTestsPublicData,
		loading: allTestsPublicLoading,
		refetch: refetchAllTestsPublicData,
	} = useExamsForFiltersFromPublicGroupUserNotLoggedInQuery({
		skip: !shouldUsePublicLoggedOutQuery,
		variables: {
			input: {
				filters: {
					difficultyLevel: [],
					ignoreExamsConnectedToLessons: false,
					tags: [],
				},
				pagination: {
					limit: 500,
					offset: 0,
				},
			},
		},
	});

	useEffect(() => {
		if (!resultSaveVersion) return;

		if (shouldUsePrivateGroupQuery) {
			refetchAllTestsData({
				code: chosenGroup || GENERAL_GROUP_NAME,
				input: {
					filters: {
						difficultyLevel: [],
						ignoreExamsConnectedToLessons: false,
						tags: [],
					},
					pagination: {
						limit: 500,
						offset: 0,
					},
				},
			});
			return;
		}

		if (shouldUsePublicLoggedOutQuery) {
			refetchAllTestsPublicData({
				input: {
					filters: {
						difficultyLevel: [],
						ignoreExamsConnectedToLessons: false,
						tags: [],
					},
					pagination: {
						limit: 500,
						offset: 0,
					},
				},
			});
		}
	}, [
		chosenGroup,
		refetchAllTestsData,
		refetchAllTestsPublicData,
		resultSaveVersion,
		shouldUsePrivateGroupQuery,
		shouldUsePublicLoggedOutQuery,
	]);

	const {
		data: filteredTests,
		loading,
		error,
	} = useExamsForFiltersFromGroupQuery({
		skip: !shouldUsePrivateGroupQuery,
		variables: {
			code: chosenGroup || GENERAL_GROUP_NAME,
			input: {
				filters: {
					difficultyLevel: selectedDifficultyLevels,
					ignoreExamsConnectedToLessons: false,
					tags: selectedTags,
				},
				pagination: {
					limit: 500,
					offset: 0,
				},
			},
		},
	});

	const {
		data: filteredPublicTests,
		loading: loadingPublic,
		error: errorPublic,
	} = useExamsForFiltersFromPublicGroupUserNotLoggedInQuery({
		skip: !shouldUsePublicLoggedOutQuery,
		variables: {
			input: {
				filters: {
					difficultyLevel: selectedDifficultyLevels,
					ignoreExamsConnectedToLessons: false,
					tags: selectedTags,
				},
				pagination: {
					limit: 500,
					offset: 0,
				},
			},
		},
	});

	const allTests = shouldUsePrivateGroupQuery
		? allTestsData?.group?.exams.items || []
		: allTestsPublicData?.publicGroups?.[0]?.exams.items || [];

	const possibleTags: string[] = useMemo(() => {
		const tags = allTests.reduce<string[]>((acc, test) => acc.concat(test.tags), []);
		return Array.from(new Set(tags)).sort((a, b) => a.localeCompare(b));
	}, [allTests]);

	const tests = shouldUsePrivateGroupQuery
		? filteredTests?.group?.exams.items || []
		: filteredPublicTests?.publicGroups?.[0]?.exams.items || [];

	const resultSummary = useMemo(() => {
		if (!isUserLoggedIn) {
			return {
				failed: 0,
				fullSuccess: 0,
				halfSuccess: 0,
				notDone: 0,
			};
		}
		return tests.reduce(
			(acc, test) => {
				switch (test.myHighestResult) {
					case HighestExamResultView.FullSuccess:
						acc.fullSuccess += 1;
						return acc;
					case HighestExamResultView.HalfSuccess:
						acc.halfSuccess += 1;
						return acc;
					case HighestExamResultView.Failed:
						acc.failed += 1;
						return acc;
					default:
						acc.notDone += 1;
						return acc;
				}
			},
			{
				failed: 0,
				fullSuccess: 0,
				halfSuccess: 0,
				notDone: 0,
			}
		);
	}, [tests, isUserLoggedIn]);

	const numberOfTests = shouldUsePrivateGroupQuery
		? filteredTests?.group?.exams.total || 0
		: filteredPublicTests?.publicGroups?.[0]?.exams.total || 0;

	const isLoading = shouldUsePrivateGroupQuery ? loading || allTestsLoading : loadingPublic || allTestsPublicLoading;
	const queryError = shouldUsePrivateGroupQuery ? error : errorPublic;

	const toggleTag = (tag: string) => {
		if (localFilters.includes(tag)) {
			setLocalFilters((prev) => prev.filter((i) => i !== tag));
		} else {
			setLocalFilters((prev) => [...prev, tag]);
		}
	};

	return (
		<Drawer isOpened={isOpened} onClose={onClose}>
			<div className="allTests__wrapper">
				<p className="allTests__header">Tagi:</p>
				<div className="allTests__tags" ref={ref}>
					{possibleTags.map((tag) => (
						<button
							onClick={() => {
								toggleTag(tag);
							}}
							key={tag}
							className={`allTests__tag ${localFilters.includes(tag) ? '' : 'inactive'}`}
						>
							{tag}
						</button>
					))}
				</div>
				<p className="allTests__header">Poziomy trudności:</p>
				<div className="allTags__levels">
					{LEVELS.map((level) => (
						<button
							onClick={() => {
								toggleTag(level.id);
							}}
							key={level.id}
							className={`allTests__tag ${level.className} ${localFilters.includes(level.id) ? '' : 'inactive'}`}
						>
							{level.label}
						</button>
					))}
				</div>

				<div className="allTests__headerRow">
					<p className="allTests__header">Testy ({numberOfTests}):</p>
				</div>

				{!isUserLoggedIn ? (
					<p className="allTests__login">
						<Link to="/login">Zaloguj się</Link> aby zobaczyć swój progres.
					</p>
				) : (
					<>
						<ul
							className="allTests__legend"
							aria-label={`Testy ${numberOfTests}: ${resultSummary.fullSuccess}/${resultSummary.halfSuccess}/${resultSummary.failed}/${resultSummary.notDone}`}
						>
							<li className="allTests__legend-item">
								<span
									className="allTests__legend-square allTests__legend-square--green-500"
									aria-hidden="true"
								/>
								<span className="allTests__legend-count">{resultSummary.fullSuccess}</span>
								<span className="allTests__legend-label">Dobrze</span>
							</li>
							<li className="allTests__legend-item">
								<span
									className="allTests__legend-square allTests__legend-square--green-300"
									aria-hidden="true"
								/>
								<span className="allTests__legend-count">{resultSummary.halfSuccess}</span>
								<span className="allTests__legend-label">Prawie dobrze</span>
							</li>
							<li className="allTests__legend-item">
								<span
									className="allTests__legend-square allTests__legend-square--red-500"
									aria-hidden="true"
								>
									✕
								</span>
								<span className="allTests__legend-count">{resultSummary.failed}</span>
								<span className="allTests__legend-label">Źle</span>
							</li>
							<li className="allTests__legend-item">
								<span
									className="allTests__legend-square allTests__legend-square--white"
									aria-hidden="true"
								/>
								<span className="allTests__legend-count">{resultSummary.notDone}</span>
								<span className="allTests__legend-label">Niezrobione</span>
							</li>
						</ul>
						<p className="allTests__login">
							Kliknij w kwadrat, aby przejść do testu, lub użyj przycisku losuj. Testy będą losowane tak,
							abyś mógł poprawiać swoje wyniki.
						</p>
					</>
				)}

				<button
					className="allTests__draw button button__draw"
					onClick={() => {
						drawTest();
						onClose();
					}}
				>
					Losuj test
				</button>
				<div className="allTests__container-inner">
					{!!tests && tests.length
						? tests.map((item) => (
								<TestItem item={item} closeDrawer={onClose} key={item.id} drawTestById={drawTestById} />
							))
						: null}
					{isLoading && (
						<p className="allTests__loading" aria-busy="true">
							<Refresh className="allTests__spinner" />
						</p>
					)}
					{!isLoading && !queryError && !tests.length && (
						<p className="allTests__noTests">Brak testow spelniajacych podane kryteria.</p>
					)}
				</div>
			</div>
		</Drawer>
	);
};
